import $ from 'jquery';
import ajaxService from '../../../services/ajaxService';
import {renderFirstReCaptcha} from '../../../components/reCaptchaHelpers';
import EmailPasswordForm from './EmailPasswordForm';
import RequestPasswordResetForm from './RequestPasswordResetForm';
import RequestTripReadAccessForm from './RequestTripReadAccessForm';

class LoginForm extends EmailPasswordForm {
	#requireCaptcha = false;
	#requestPasswordResetForm = null;
	#requestTripReadAccessForm = null;
	
	/**
	 * @param {Element} form
	 * @param {Object}  params
	 */
	constructor(form, params) {
		super(form, params);
		
		this.#initEvents();
	}
	
	#initEvents() {
		this.getElement('.requestPasswordResetButton').addEventListener(
			'click',
			(ev) => {
				this.#switchToRequestPasswordResetForm();
				
				ev.preventDefault();
			}
		);
		
		this.getElement('.switch-to-request-trip-read-access-form').addEventListener(
			'click',
			(ev) => {
				this.#switchToRequestTripReadAccessForm();

				ev.preventDefault();
			}
		);
		
		this.#getRequestTripReadAccessForm().querySelector('.switch-to-login-form').addEventListener(
			'click',
			(ev) => {
				this.#switchToLoginForm();

				ev.preventDefault();
			}
		);
	}
	
	#renderFirstReCaptcha() {
		renderFirstReCaptcha({
			'renderParams': {
				'element': this.getElement('.g-recaptcha'),
				'params' : {
					'callback': () => {
						this.submitForm();
					}
				}
			}
		});
	}
	
	isValidForm() {
		return super.isValidForm() && this.#isReCaptchaChallengeDone();
	}
	
	sendCustomXhrRequest() {
		const formData = new FormData(this.getForm());
		
		return ajaxService.sendPost({
			'url' : 'user/login',
			'data': {
				'email'         : formData.get('email'),
				'password'      : formData.get('password'),
				'reCaptchaToken': window.grecaptcha ? window.grecaptcha.getResponse() : ''
			}
		});
	}
	
	onCustomXhrError(responseObject) {
		const {responseJSON = {}} = responseObject,
		      errorCode           = responseJSON.error && responseJSON.error.code;
		
		// if the backend says that the user needs captcha, then
		if (errorCode === 'MISSING_RE_CAPTCHA_TOKEN') {
			this.#requireCaptcha = true;
			this.#renderFirstReCaptcha();
		}
		
		// reset the reCAPTCHA if it is already available and needed for the next try
		if (this.#requireCaptcha && window.grecaptcha) {
			window.grecaptcha.reset();
		}
		
		this.#initResendConfirmationEmailBlock();
	}
	
	#switchToLoginForm() {
		$(this.#getRequestTripReadAccessForm()).slideUp(
			300,
			() => {
				$(this.getForm()).slideDown(300);
			}
		);
	}
	
	#switchToRequestPasswordResetForm() {
		$(this.getForm()).slideUp(
			300,
			this.#showRequestPasswordResetForm.bind(this)
		);
	}
	
	#switchToRequestTripReadAccessForm() {
		$(this.getForm()).slideUp(
			300,
			this.#showRequestTripReadAccessForm.bind(this)
		);
	}
	
	#showRequestPasswordResetForm() {
		const requestPasswordResetForm = document.querySelector('.requestPasswordResetForm'),
		      $container               = $(requestPasswordResetForm.parentElement);

		$container.add($(requestPasswordResetForm)).slideDown(300);

		if (!this.#requestPasswordResetForm) {
			this.#requestPasswordResetForm = new RequestPasswordResetForm(requestPasswordResetForm, this.getForm());
		}
	}
	
	#showRequestTripReadAccessForm() {
		$(this.#getRequestTripReadAccessForm()).slideDown(300);
		
		if (!this.#requestTripReadAccessForm) {
			this.#requestTripReadAccessForm = new RequestTripReadAccessForm();
		}
	}
	
	#isReCaptchaChallengeDone() {
		const isReCaptchaRendered = this.getElement('.g-recaptcha').childElementCount > 0;
		
		return !isReCaptchaRendered || !!window.grecaptcha.getResponse();
	}
	
	#initResendConfirmationEmailBlock() {
		const resendConfirmationEmailButton = this.getElement('.resend-confirmation-email');
		
		if (resendConfirmationEmailButton) {
			resendConfirmationEmailButton.addEventListener(
				'click',
				this.#handleResendConfirmationEmailRequest.bind(this),
				{
					'once': true
				}
			);
		}
	}
	
	async #handleResendConfirmationEmailRequest() {
		const result = await this.resendConfirmationEmailRequest();
		
		this.#processConfirmationEmailResentResponse(result);
	}
	
	resendConfirmationEmailRequest() {
		const formData = new FormData(this.getForm());
		
		return ajaxService.sendPost({
			'url' : 'user/resend-confirmation-email',
			'data': {
				'email': formData.get('email')
			}
		});
	}
	
	#processConfirmationEmailResentResponse({response, xhr}) {
		const responseObject = typeof xhr === 'object' ? xhr : response;
		
		responseObject
			.then(
				() => {
					this.getFormErrorHandler().hideFeedbackMessages();
					this.getElement('.confirmationEmailResentSuccessMessage').classList.remove('hidden-element');
				}
			)
			.catch(() => {
				this.getFormErrorHandler().handleErrorResponse(responseObject);
			});
	}
	
	/** @returns {HTMLFormElement} */
	#getRequestTripReadAccessForm() {
		return document.querySelector('.request-trip-read-access-form');
	}
}

export default LoginForm;